/**
 * The command palette's index (DASHBOARD.md §1.17). Bots, Action Center items,
 * Grant asks and instruction threads come from the snapshot already in the
 * browser; recommendation history and documents come from GET /search (the slim
 * snapshot leaves them out). Ranking: prefix, then word start, then anywhere;
 * ties go by kind, then label.
 */

import { Snapshot } from './types';
import { botBySlug } from './selectors';

export type PaletteKind = 'command' | 'bot' | 'action' | 'recommendation' | 'document' | 'ga' | 'thread';

export interface PaletteItem {
  id: string;
  kind: PaletteKind;
  label: string;
  hint?: string;
  /** A route to open, or a command id the host runs. */
  to?: string;
  command?: CommandId;
  /** Extra words that should also match. */
  keywords?: string;
}

export type CommandId = 'sync' | 'theme' | 'prefs' | 'views' | 'shortcuts';

const BASE = '/dashboard/bot-ops';
export const KIND_ORDER: Record<PaletteKind, number> = { command: 0, bot: 1, action: 2, recommendation: 3, ga: 4, thread: 5, document: 6 };
export const KIND_LABEL: Record<PaletteKind, string> = { command: 'Command', bot: 'Bot', action: 'Item', recommendation: 'Recommendation', document: 'Document', ga: 'Grant ask', thread: 'Thread' };

export const COMMANDS: PaletteItem[] = [
  { id: 'cmd-sync', kind: 'command', label: 'Sync now', command: 'sync', keywords: 'refresh update' },
  { id: 'cmd-ops', kind: 'command', label: 'Go to Operations', to: BASE, keywords: 'home overview' },
  { id: 'cmd-activity', kind: 'command', label: 'Go to Activity', to: `${BASE}/activity` },
  { id: 'cmd-scorecard', kind: 'command', label: 'Go to Scorecard', to: `${BASE}/scorecard` },
  { id: 'cmd-audit', kind: 'command', label: 'Go to Audit trail', to: `${BASE}/audit`, keywords: 'log history' },
  { id: 'cmd-theme', kind: 'command', label: 'Toggle theme', command: 'theme', keywords: 'dark light mode' },
  { id: 'cmd-prefs', kind: 'command', label: 'Notification preferences', command: 'prefs', keywords: 'settings digest alerts' },
  { id: 'cmd-views', kind: 'command', label: 'Saved views', command: 'views', keywords: 'bookmarks' },
  { id: 'cmd-keys', kind: 'command', label: 'Keyboard shortcuts', command: 'shortcuts', keywords: 'help keys' },
];

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

export function buildIndex(s: Snapshot): PaletteItem[] {
  const out: PaletteItem[] = [];
  for (const b of s.bots) {
    out.push({ id: `bot-${b.slug}`, kind: 'bot', label: b.name, hint: `${s.companies[b.company] ? s.companies[b.company].name : b.company} · ${b.tier}`, to: `${BASE}/bots/${encodeURIComponent(b.slug)}`, keywords: `${b.short} ${b.slug}` });
    for (const a of b.actions || []) {
      const title = a.for || a.text || '';
      if (!title) continue;
      out.push({ id: `action-${a.id}`, kind: 'action', label: clip(title, 90), hint: b.name, to: `${BASE}?company=${b.company}&action=${encodeURIComponent(a.id)}`, keywords: a.id });
    }
  }
  for (const r of s.ledger || []) {
    out.push({ id: `ga-${r.id}`, kind: 'ga', label: `${r.id} · ${clip(r.title || '', 80)}`, hint: r.status, to: `${BASE}?ga=${encodeURIComponent(r.id)}` });
  }
  for (const i of s.instructions || []) {
    const bot = botBySlug(s, i.target_bot_slug);
    out.push({ id: `thread-${i.id}`, kind: 'thread', label: clip(i.grant_instruction || 'Instruction', 90), hint: bot ? bot.name : i.target_bot_slug, to: `${BASE}/bots/${encodeURIComponent(i.target_bot_slug)}?tab=inbox&instruction=${encodeURIComponent(i.id)}` });
  }
  return out;
}

export interface ServerHits {
  recommendations: { id: string; title: string; bot_slug: string; bot_name: string; company: string; date?: string | null }[];
  documents: { title: string; filename?: string | null; sha256: string; type?: string | null; bot_slug: string; bot_name: string; company: string }[];
}

export function fromServer(h: ServerHits | null | undefined): PaletteItem[] {
  if (!h) return [];
  return [
    ...h.recommendations.map((r): PaletteItem => ({ id: `rec-${r.bot_slug}-${r.id}`, kind: 'recommendation', label: clip(r.title, 90), hint: r.bot_name, to: `${BASE}/bots/${encodeURIComponent(r.bot_slug)}?tab=recommendations&rec=${encodeURIComponent(r.id)}` })),
    ...h.documents.map((d): PaletteItem => ({ id: `doc-${d.bot_slug}-${d.sha256}`, kind: 'document', label: d.title, hint: d.bot_name, to: `${BASE}/bots/${encodeURIComponent(d.bot_slug)}?tab=documents` })),
  ];
}

/** 0 prefix · 1 word start · 2 contains · -1 none. */
export function matchRank(text: string, q: string): number {
  const t = text.toLowerCase();
  if (t.startsWith(q)) return 0;
  if (t.includes(` ${q}`) || t.includes(`-${q}`) || t.includes(`/${q}`)) return 1;
  return t.includes(q) ? 2 : -1;
}

/** `ask cfo` → the bots matching "cfo", each opening its Inbox with the composer focused. */
export function parseAsk(query: string): string | null {
  const m = /^\s*ask\s+(.*)$/i.exec(query);
  return m ? m[1].trim().toLowerCase() : null;
}

export function askItems(s: Snapshot, term: string): PaletteItem[] {
  return s.bots
    .filter((b) => !term || `${b.name} ${b.short} ${b.slug}`.toLowerCase().includes(term))
    .map((b): PaletteItem => ({ id: `ask-${b.slug}`, kind: 'bot', label: `Ask ${b.name}…`, hint: 'opens the Inbox', to: `${BASE}/bots/${encodeURIComponent(b.slug)}?tab=inbox&compose=1` }));
}

export const MAX_RESULTS = 30;

export function search(index: PaletteItem[], query: string, s?: Snapshot): PaletteItem[] {
  const ask = parseAsk(query);
  if (ask !== null && s) return askItems(s, ask).slice(0, MAX_RESULTS);
  const q = query.trim().toLowerCase();
  if (!q) return COMMANDS.concat(index.filter((i) => i.kind === 'bot').slice(0, 6));
  const scored: { it: PaletteItem; r: number }[] = [];
  for (const it of COMMANDS.concat(index)) {
    const kw = it.keywords ? matchRank(it.keywords, q) : -1;
    const r = Math.min(...[matchRank(it.label, q), kw >= 0 ? kw + 1 : -1].filter((x) => x >= 0).concat([99])); // a keyword hit ranks just below the same hit in the label
    if (r < 99) scored.push({ it, r });
  }
  scored.sort((a, b) => a.r - b.r || KIND_ORDER[a.it.kind] - KIND_ORDER[b.it.kind] || a.it.label.localeCompare(b.it.label));
  return scored.slice(0, MAX_RESULTS).map((x) => x.it);
}
